import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { DOCUMENT, NgClass } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ROUTES } from './sidebar-items';
import { RouteInfo } from './sidebar.metadata';
import { TranslateModule } from '@ngx-translate/core';
import { NgScrollbar } from 'ngx-scrollbar';
import { UnsubscribeOnDestroyAdapter } from '@shared';
import { AuthManagementService } from '@core/service/auth-management.service';
import { UserRole } from '@core/models/Enums/UserRole';
import { BusinessGroup } from '@core/models/BusinessGroup';
import { BusinessGroupService } from '@core/service/business-group.service';
import { GEMPService } from '@core/http/table-data-services/GEMP.service';
import { environment } from '@enviroment/environment';
import { RegionWordPipe } from '@core/pipes/region-word.pipe';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [NgScrollbar, RouterLinkActive, RouterLink, NgClass, TranslateModule, RegionWordPipe],
})
export class SidebarComponent extends UnsubscribeOnDestroyAdapter implements OnInit, OnDestroy {
  public sidebarItems!: RouteInfo[];
  public innerHeight?: number;
  public bodyTag!: HTMLElement;
  listMaxHeight?: string;
  listMaxWidth?: string;
  userFullName?: string;
  isPatient?: boolean;
  groupImg: string = 'assets/images/user/placeholder.png';
  userType?: string;
  empresa?: string;
  headerHeight = 60;
  businessGroupId: string = '';
  currentRole: UserRole | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private authManagement: AuthManagementService,
    private businessGroupService: BusinessGroupService,
    private gempService: GEMPService,
    private router: Router,
  ) {
    super();
    this.elementRef.nativeElement.closest('body');
    this.subs.sink = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.renderer.removeClass(this.document.body, 'overlay-open');
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  windowResizecall() {
    this.setMenuHeight();
    this.checkStatuForResize();
  }

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.renderer.removeClass(this.document.body, 'overlay-open');
    }
  }

  callToggleMenu(event: Event, length: number) {
    if (length > 0) {
      const parentElement = (event.target as HTMLInputElement).closest('li');
      const activeClass = parentElement?.classList.contains('active');
      if (activeClass) this.renderer.removeClass(parentElement, 'active');
      else this.renderer.addClass(parentElement, 'active');
    }
  }

  ngOnInit() {
    const userData = this.authManagement.userData();
    const currentRole = this.authManagement.getRole();
    this.currentRole = currentRole;

    const initialId =
      sessionStorage.getItem('businessGroupId') ||
      localStorage.getItem('businessGroupId') ||
      userData?.IdGEMP ||
      '';

    if (initialId) {
      this.businessGroupId = initialId;
      this.loadBusinessGroupById(initialId);
    }

    this.subs.sink = this.businessGroupService.businessGroupId$.subscribe((id) => {
      if (!id || id === this.businessGroupId) return;
      this.businessGroupId = id;
      this.loadBusinessGroupById(id);
    });

    this.userFullName = userData?.unique_name || '';
    this.userType = currentRole || '';
    this.isPatient = currentRole === UserRole.Paciente;

    this.sidebarItems = this.buildSidebarByRoleAndRegion(currentRole);

    this.initLeftSidebar();
    this.bodyTag = this.document.body;
  }

  get showBusinessGroupName(): boolean {
    const isSpain = String(environment.region ?? '').toUpperCase() === 'ES';

    if (!isSpain) {
      return true;
    }

    return ![
      UserRole.Medico,
      UserRole.Particular,
      UserRole.Paciente
    ].includes(this.currentRole as UserRole);
  }

  private buildSidebarByRoleAndRegion(currentRole: UserRole | null): RouteInfo[] {
    const filtered = ROUTES
      .map(item => this.mapAndFilterItem(item, currentRole))
      .filter((item): item is RouteInfo => item !== null);

    return this.removeEmptyGroupTitles(filtered);
  }

  private mapAndFilterItem(item: RouteInfo, currentRole: UserRole | null): RouteInfo | null {
    const submenu = (item.submenu || [])
      .map(sub => this.mapAndFilterItem(sub, currentRole))
      .filter((sub): sub is RouteInfo => sub !== null);

    const roleAllowed = this.hasRoleAccess(item, currentRole);
    const regionAllowed = this.isRegionAllowed(item);

    if (item.groupTitle) {
      return {
        ...item,
        submenu,
      };
    }

    if (!roleAllowed || !regionAllowed) {
      return null;
    }

    return {
      ...item,
      submenu,
    };
  }

  private hasRoleAccess(item: RouteInfo, currentRole: UserRole | null): boolean {
    if (item.groupTitle) return true;
    if (item.role?.includes(UserRole.All)) return true;
    if (!currentRole) return false;
    return item.role?.includes(currentRole);
  }

  private isRegionAllowed(item: RouteInfo): boolean {
    const excludedRegions = (item.excludeRegions ?? [])
      .map(region => String(region).trim().toUpperCase());

    if (!excludedRegions.length) {
      return true;
    }

    const currentRegion = String(environment.region ?? '')
      .trim()
      .toUpperCase();

    if (!currentRegion) {
      return true;
    }

    return !excludedRegions.includes(currentRegion);
  }

  private removeEmptyGroupTitles(items: RouteInfo[]): RouteInfo[] {
    return items.filter((item, index) => {
      if (!item.groupTitle) return true;

      const nextItem = items[index + 1];

      if (!nextItem) return false;

      if (nextItem.groupTitle) return false;

      return true;
    });
  }

  public getTourId(item: any): string | null {
    if (!item || item.groupTitle) return null;

    const path = String(item.path || '').trim();
    if (!path) return null;

    const normalized = path
      .replace(/^\//, '')
      .replace(/\//g, '-')
      .replace(/\s+/g, '-')
      .toLowerCase();

    return `sidebar-${normalized}`;
  }

  private loadBusinessGroupById(id: string): void {
    this.subs.sink = this.gempService.getById(id).subscribe({
      next: (res) => {
        const bg: BusinessGroup | undefined = res?.data;
        this.empresa = bg?.nombre ?? '';
        this.groupImg = bg?.logoBase64
          ? 'data:image/png;base64,' + bg.logoBase64
          : 'assets/images/user/placeholder.png';
      },
      error: () => {
        this.empresa = '';
        this.groupImg = 'assets/images/user/placeholder.png';
      }
    });
  }

  initLeftSidebar() {
    const _this = this;
    _this.setMenuHeight();
    _this.checkStatuForResize();
  }

  setMenuHeight() {
    this.innerHeight = window.innerHeight;
    const height = this.innerHeight - this.headerHeight;
    this.listMaxHeight = height + '';
    this.listMaxWidth = '500px';
  }

  checkStatuForResize() {
    if (window.innerWidth < 1025) {
      this.renderer.addClass(this.document.body, 'ls-closed');
    } else {
      this.renderer.removeClass(this.document.body, 'ls-closed');
    }
  }

  mouseHover() {
    const body = this.elementRef.nativeElement.closest('body');
    if (body.classList.contains('submenu-closed')) {
      this.renderer.addClass(this.document.body, 'side-closed-hover');
    }
  }

  mouseOut() {
    const body = this.elementRef.nativeElement.closest('body');
    if (body.classList.contains('submenu-closed')) {
      this.renderer.removeClass(this.document.body, 'side-closed-hover');
    }
  }
}
